import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Menu } from "lucide-react";

import { useBootstrap } from "../api/queries/useBootstrap";
import { Button } from "../components/ui/button";
import { AccountMenu } from "./AccountMenu";
import { BrandMark } from "./BrandMark";
import { CurrentPlanLabel } from "./CurrentPlanLabel";
import { EnvironmentBadge } from "./EnvironmentBadge";
import { ScopeName, ScopeSwitcher, isScopeKeyedPath } from "./ScopeSwitcher";
import { NavSidebar } from "./NavSidebar";
import { ThemeToggle } from "./ThemeToggle";
import { useCurrentPath, useRouteTitle } from "./useRouteTitle";
import "../components/touch/touchScale.css";

/** The same furniture as desktop, on a narrow icon rail that expands on tap. No hover. */
export function AppShellTablet({ children }: { children: ReactNode }) {
  const { data } = useBootstrap();
  const title = useRouteTitle();
  const pathname = useCurrentPath();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        className={`touch-scale sticky top-0 flex h-screen shrink-0 flex-col border-r border-border bg-surface-sunken ${
          expanded ? "w-60" : "w-14"
        }`}
      >
        <div className="flex h-12 items-center justify-center border-b border-border">
          <Link to="/" className="text-primary">
            <BrandMark className="h-5 w-7" />
          </Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavSidebar
            mode={expanded ? "full" : "rail"}
            touch
            onNavigate={() => setExpanded(false)}
          />
        </div>
        <div className="border-t border-border p-xs">
          <Button
            variant="ghost"
            size="icon"
            className="size-11"
            aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
            onClick={() => setExpanded((e) => !e)}
          >
            <Menu className="size-5" />
          </Button>
        </div>
      </aside>

      <div
        className="touch-scale flex min-w-0 flex-1 flex-col"
        onPointerDown={() => {
          if (expanded) setExpanded(false);
        }}
      >
        <header className="sticky top-0 z-10 flex h-12 items-center gap-sm border-b border-border bg-background px-md">
          <span className="type-subhead text-foreground">{data?.tenant.name}</span>
          <EnvironmentBadge />
          <div className="flex-1" />
          <ScopeSwitcher />
          <CurrentPlanLabel />
          <ThemeToggle />
          <AccountMenu />
        </header>
        <div className="border-b border-border px-md py-sm">
          <h1 className="type-section text-foreground">
            {title}
            {isScopeKeyedPath(pathname) ? <ScopeName className="ml-xs" /> : null}
          </h1>
        </div>
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
